<div class="min-h-screen space-y-4 bg-white p-4 sm:p-6">
  <!-- Toolbar -->
  <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
    <div>
      <h2 class="text-lg font-bold text-[#1E2A78]">{{ 'DISB_REPAYMENT.REPAYMENT_SCHEDULE' | translate }}</h2>
      <p class="mt-0.5 text-xs text-slate-500">
        {{ 'DISB_REPAYMENT.INSTALLMENTS_EDITABLE_HINT' | translate }}
      </p>
    </div>

    <div class="flex items-center gap-2">
      <button
        *ngIf="editable"
        type="button"
        [disabled]="calculating"
        (click)="calculateRepaymentPlan()"
        class="inline-flex items-center gap-1.5 rounded-md bg-[#1E2A78] px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-[#16205e] disabled:cursor-not-allowed disabled:opacity-50"
      >
        {{ (calculating ? 'DISB_REPAYMENT.CALCULATING' : 'DISB_REPAYMENT.CALCULATE') | translate }}
      </button>

      <button
        type="button"
        (click)="openPreviewModal()"
        class="inline-flex items-center gap-1.5 rounded-md border border-indigo-200 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700 transition hover:bg-indigo-50"
      >
        {{ 'DISB_REPAYMENT.VIEW_CALCULATED_DATA' | translate }}
      </button>

      <span
        *ngIf="editable"
        class="inline-flex items-center rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700"
      >
        {{ 'DISB_REPAYMENT.EDITABLE_MODE' | translate }}
      </span>
    </div>
  </div>

  <!-- Messages -->
  <div
    *ngIf="errorMessage"
    class="rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700"
  >
    {{ errorMessage }}
  </div>

  <div
    *ngIf="successMessage"
    class="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-medium text-emerald-700"
  >
    {{ successMessage }}
  </div>

  <!-- Empty state -->
  <div
    *ngIf="repaymentRows.length === 0"
    class="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center shadow-sm"
  >
    <h3 class="text-base font-semibold text-slate-800">
      {{ 'DISB_REPAYMENT.PLAN_NOT_CALCULATED' | translate }}
    </h3>
    <p class="mt-2 text-sm text-slate-500">
      {{ 'DISB_REPAYMENT.CLICK_CALCULATE_HINT' | translate }}
    </p>
  </div>

  <!-- Tables: one schedule per repayment term type (e.g. Conventional / Islamic) -->
  @if (repaymentRows.length > 0) {
    <div class="space-y-4">
      @for (group of termGroups; track group.termRefCdSeq; let first = $first) {
        @if (getTermLabel(group.termRefCdSeq)) {
          <h3 class="text-base font-bold text-[#1E2A78]">
            {{ getTermLabel(group.termRefCdSeq) | translate }}
          </h3>
        }

        <div class="overflow-hidden rounded-xl border border-indigo-200 shadow-sm">
          <div
            class="border-b border-indigo-200 bg-[#1E2A78] px-3 py-2 text-center text-sm font-bold uppercase tracking-wide text-white"
          >
            {{ 'DISB_REPAYMENT.TENTATIVE_PLAN_TITLE' | translate }}
          </div>

          <ng-container
            *ngTemplateOutlet="
              scheduleTable;
              context: {
                allowEdit: editable,
                rows: group.rows,
                columns: group.columns,
                showOpeningExposure: first
              }
            "
          ></ng-container>
        </div>
      }
    </div>
  }

  <div
    *ngIf="repaymentRows.length > 0"
    class="flex flex-col gap-1 text-xs text-slate-600 sm:flex-row sm:items-center sm:justify-between"
  >
    <span>
      {{ 'DISB_REPAYMENT.ROWS_PERIODS_COUNT' | translate: { rows: repaymentRows.length, periods: columns.length } }}
    </span>

    <span
      class="font-semibold"
      [ngClass]="
        isGrandTotalExceeded || isGrandTotalUnderAllocated
          ? 'text-red-600'
          : 'text-[#1E2A78]'
      "
    >
      {{ 'DISB_REPAYMENT.TOTAL_DISBURSEMENT' | translate }}: {{ totalDisbursementAmount | number: '1.0-2' }} ·
      {{ 'DISB_REPAYMENT.TOTAL_REPAYMENT' | translate }}: {{ grandTotal | number: '1.0-2' }} ·
      {{ 'DISB_REPAYMENT.REMAINING' | translate }}: {{ remainingAmount | number: '1.0-2' }}
    </span>
  </div>

  <!-- Validation -->
  <div
    *ngIf="repaymentRows.length > 0 && hasValidationError"
    class="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
  >
    <p *ngIf="hasRowLimitError" class="font-semibold">
      {{ 'DISB_REPAYMENT.ERR_ROW_TOTALS_EXCEED' | translate }}
    </p>
    <p *ngIf="hasRowUnderAllocationError" class="font-semibold">
      {{ 'DISB_REPAYMENT.ERR_ROW_TOTALS_UNDER' | translate }}
    </p>
    <p *ngIf="isGrandTotalExceeded" class="mt-1 font-semibold">
      {{ 'DISB_REPAYMENT.TOTAL_EXCEEDS_BY' | translate: { amount: (-remainingAmount | number: '1.0-2') } }}
    </p>
    <p *ngIf="isGrandTotalUnderAllocated" class="mt-1 font-semibold">
      {{ 'DISB_REPAYMENT.TOTAL_UNDER_BY' | translate: { amount: (remainingAmount | number: '1.0-2') } }}
    </p>
  </div>

  <!-- Calculated Data Preview Modal -->
  <div
    *ngIf="showPreviewModal"
    class="fixed inset-0 z-100000 flex items-center justify-center bg-slate-900/60 p-4"
    (click)="closePreviewModal()"
  >
    <div
      class="flex h-[96vh] w-full max-w-[95vw] flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
      (click)="$event.stopPropagation()"
    >
      <!-- Modal header -->
      <div
        class="flex shrink-0 items-center justify-between gap-4 bg-[#1E2A78] px-5 py-4"
      >
        <h3 class="text-base font-bold uppercase tracking-wide text-white">
          {{ 'DISB_REPAYMENT.TENTATIVE_PLAN_TITLE' | translate }}
        </h3>

        <div class="flex shrink-0 items-center gap-2">
          <button
            *ngIf="editable"
            type="button"
            (click)="togglePreviewEditMode()"
            class="inline-flex h-9 items-center rounded-md border border-white/40 px-4 text-sm font-semibold text-white transition hover:bg-white/10"
          >
            {{ (previewEditMode ? 'DISB_REPAYMENT.DONE_EDITING' : 'COMMON.EDIT') | translate }}
          </button>

          <button
            type="button"
            (click)="closePreviewModal()"
            [attr.aria-label]="'COMMON.CLOSE' | translate"
            class="inline-flex h-9 w-9 items-center justify-center rounded-md border border-white/40 text-white transition hover:bg-white/10"
          >
            ✕
          </button>
        </div>
      </div>

      <!-- Modal body -->
      <div class="flex-1 space-y-6 overflow-auto p-5">
        @for (group of termGroups; track group.termRefCdSeq; let first = $first) {
          <div>
            @if (getTermLabel(group.termRefCdSeq)) {
              <h4 class="mb-2 text-sm font-bold uppercase tracking-wide text-[#1E2A78]">
                {{ getTermLabel(group.termRefCdSeq) | translate }}
              </h4>
            }

            <ng-container
              *ngTemplateOutlet="
                scheduleTable;
                context: {
                  allowEdit: previewEditMode,
                  rows: group.rows,
                  columns: group.columns,
                  showOpeningExposure: first
                }
              "
            ></ng-container>
          </div>
        }
      </div>
    </div>
  </div>
</div>

<!-- Shared spreadsheet-style table, used by both the inline view and the popup -->
<ng-template
  #scheduleTable
  let-allowEdit="allowEdit"
  let-rows="rows"
  let-columns="columns"
  let-showOpeningExposure="showOpeningExposure"
>
  <div class="w-full overflow-x-auto">
    <table class="w-full border-collapse text-base">
      <thead>
        <tr>
          <th
            colspan="3"
            class="border border-indigo-300 bg-[#1E2A78] px-4 py-2.5 text-start font-semibold uppercase tracking-wide text-white"
          >
            {{ 'DISB_REPAYMENT.DISBURSEMENTS' | translate }}
          </th>

          <th
            [attr.colspan]="columns.length + 1"
            class="border border-indigo-300 bg-[#1E2A78] px-4 py-2.5 text-center font-semibold uppercase tracking-wide text-white"
          >
            {{ 'DISB_REPAYMENT.REPAYMENT_SCHEDULE' | translate }}
          </th>
        </tr>

        <tr>
          <th class="min-w-[130px] border border-indigo-300 bg-[#1E2A78] px-4 py-3 text-start font-semibold text-white">
            {{ 'DISB_REPAYMENT.QUARTER' | translate }}
          </th>

          <th class="min-w-[110px] border border-indigo-300 bg-[#1E2A78] px-4 py-3 text-start font-semibold text-white">
            {{ 'DISB_REPAYMENT.MONTH' | translate }}
          </th>

          <th class="min-w-[130px] border border-indigo-300 bg-[#1E2A78] px-4 py-3 text-end font-semibold text-white">
            {{ 'DISB_REPAYMENT.TRANCHE_AMOUNT' | translate }}
          </th>

          <th
            *ngFor="let col of columns; trackBy: trackByColumn"
            class="min-w-[112px] border border-indigo-300 bg-[#1E2A78] px-3 py-3 text-end font-semibold text-white"
            [title]="col"
          >
            {{ formatPeriod(col) }}
          </th>

          <th class="min-w-[120px] border border-indigo-300 bg-[#16205e] px-4 py-3 text-end font-semibold text-white">
            {{ 'COMMON.TOTAL' | translate }}
          </th>
        </tr>
      </thead>

      <tbody>
        @if (showOpeningExposure) {
          <tr class="bg-indigo-50">
            <td colspan="2" class="border border-indigo-100 px-4 py-3 font-semibold text-[#1E2A78]">
              {{ 'DISB_REPAYMENT.OPENING_EXPOSURE' | translate }}
            </td>

            <td class="border border-indigo-100 px-4 py-3 text-end font-semibold text-[#1E2A78]">
              {{ openingExposure | number: '1.0-2' }}
            </td>

            <td
              *ngFor="let col of columns; trackBy: trackByColumn"
              class="border border-indigo-100 px-3 py-3"
            ></td>

            <td class="border border-indigo-100 px-4 py-3"></td>
          </tr>
        }

        <tr
          *ngFor="let row of rows; trackBy: trackByRepaymentRow"
          class="bg-white transition hover:bg-indigo-50/60"
        >
          <td class="border border-indigo-100 px-4 py-3 text-slate-700">{{ row.quarter }}</td>
          <td class="border border-indigo-100 px-4 py-3 text-slate-700">{{ formatPeriod(row.month) }}</td>

          <td class="border border-indigo-100 px-4 py-3 text-end text-slate-800">
            {{ row.trancheAmount | number: '1.0-2' }}
          </td>

          <td
            *ngFor="let col of columns; trackBy: trackByColumn"
            class="border border-indigo-100 px-2 py-2 text-end align-middle"
          >
            <ng-container *ngIf="hasInstallment(row, col); else noValueCell">
              <input
                *ngIf="allowEdit; else readOnlyCell"
                type="text"
                appAmountInput
                [disabled]="saving"
                [(ngModel)]="row.schedule[col]"
                (ngModelChange)="onInstallmentChange(row, col, $event)"
                class="h-11 w-full min-w-[96px] rounded-md border bg-white px-2 text-end text-base font-semibold outline-none transition focus:ring-2"
                [ngClass]="
                  isRowTotalExceeded(row) || isRowTotalUnderAllocated(row)
                    ? 'border-red-300 text-red-700 focus:border-red-500 focus:ring-red-100'
                    : 'border-indigo-200 text-slate-800 focus:border-indigo-500 focus:ring-indigo-100'
                "
              />

              <ng-template #readOnlyCell>
                <span [ngClass]="isRowTotalExceeded(row) || isRowTotalUnderAllocated(row) ? 'text-red-700' : 'text-slate-700'">
                  {{ row.schedule[col] | number: '1.0-2' }}
                </span>
              </ng-template>
            </ng-container>

            <ng-template #noValueCell>
              <span class="text-slate-300">-</span>
            </ng-template>
          </td>

          <td
            class="border border-indigo-100 px-4 py-3 text-end font-bold"
            [ngClass]="isRowTotalExceeded(row) || isRowTotalUnderAllocated(row) ? 'bg-red-50 text-red-700' : 'text-[#1E2A78]'"
          >
            {{ getRowTotal(row) | number: '1.0-2' }}
          </td>
        </tr>

        <tr class="bg-[#1E2A78] text-white">
          <td colspan="3" class="border border-indigo-300 px-4 py-3 font-bold">{{ 'COMMON.TOTAL' | translate }}</td>

          <td
            *ngFor="let col of columns; trackBy: trackByColumn"
            class="border border-indigo-300 px-3 py-3 text-end font-semibold"
          >
            {{ getColumnTotalFor(rows, col) | number: '1.0-2' }}
          </td>

          <td
            class="border border-indigo-300 px-4 py-3 text-end font-bold bg-[#16205e]"
          >
            {{ getGrandTotalFor(rows) | number: '1.0-2' }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</ng-template>
